// S2 (v0.13.0) + v0.14.1 hotfix — tenant admin settings page.
//
// v0.14.1: added a Timezone selector. Saving flips the tenantStore's
// timezone immediately so every timestamp re-renders in the new zone
// without a reload.
import { useEffect, useState } from "react";

import {
  useTenantSettings,
  useUpdateTenantSettings,
} from "@/api/adminTenants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


// v0.14.1 — curated common timezones with their current UTC offset
// (computed at render time so DST transitions are reflected). Users
// can also type any valid IANA name; the server validates.
const COMMON_TIMEZONES: { value: string; label: string }[] = [
  { value: "Asia/Kolkata", label: "Asia/Kolkata — IST" },
  { value: "Asia/Dubai", label: "Asia/Dubai — GST" },
  { value: "Asia/Singapore", label: "Asia/Singapore — SGT" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo — JST" },
  { value: "Europe/London", label: "Europe/London — GMT/BST" },
  { value: "Europe/Berlin", label: "Europe/Berlin — CET/CEST" },
  { value: "America/New_York", label: "America/New_York — EST/EDT" },
  { value: "America/Chicago", label: "America/Chicago — CST/CDT" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles — PST/PDT" },
  { value: "UTC", label: "UTC" },
];

function offsetFor(tz: string): string {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const parts = fmt.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value;
    return tzPart ?? "";
  } catch {
    return "";
  }
}

export default function TenantSettingsPage() {
  const { data: t, isLoading, isError, error: loadError, refetch } = useTenantSettings();
  const update = useUpdateTenantSettings();
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [settingsText, setSettingsText] = useState("{}");
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!t) return;
    setName(t.name);
    setTimezone(t.timezone || "Asia/Kolkata");
    setSettingsText(JSON.stringify(t.settings_json ?? {}, null, 2));
  }, [t]);

  async function save() {
    setError(null);
    setSavedAt(null);
    let parsed: Record<string, unknown> | undefined;
    try {
      parsed = settingsText.trim() ? JSON.parse(settingsText) : {};
    } catch {
      setError("Settings JSON is not valid JSON");
      return;
    }
    try {
      await update.mutateAsync({
        name,
        timezone,
        settings_json: parsed,
      });
      setSavedAt(Date.now());
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save");
    }
  }

  // WAS: `if (!t) return <p>Loading…</p>` — the T1a defect verbatim. On a 500,
  // React Query leaves `data` undefined forever, so this page showed the word
  // "Loading…" permanently. A spinner that never resolves is the worst of the
  // four states: it says "wait" when the honest answer is "this failed".
  if (isError || (!t && !isLoading)) {
    return (
      <QueryState
        isLoading={false}
        isError
        error={loadError}
        label="tenant settings"
        onRetry={() => void refetch()}
      >
        <></>
      </QueryState>
    );
  }
  if (!t) {
    return (
      <QueryState isLoading label="tenant settings">
        <></>
      </QueryState>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Tenant Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          <span className="font-mono">{t.slug}</span> · {t.plan} · {t.status}
        </p>
      </div>
      <Card>
        <CardContent className="space-y-3 p-4">
          <div>
            <Label>Display Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <Label>Timezone</Label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label} ({offsetFor(tz.value)})
                </option>
              ))}
              {/* If the tenant uses a custom tz not in our common list,
                  surface it so the user doesn't lose their setting on
                  next save. */}
              {!COMMON_TIMEZONES.some((tz) => tz.value === timezone) && (
                <option value={timezone}>
                  {timezone} ({offsetFor(timezone)})
                </option>
              )}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              All timestamps in the UI render in this timezone. Storage
              stays UTC internally. Changes take effect immediately.
            </p>
          </div>

          <div>
            <Label>Settings JSON</Label>
            <textarea
              className="h-64 w-full rounded border border-border bg-background p-2 font-mono text-xs"
              value={settingsText}
              onChange={(e) => setSettingsText(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Keys here are merged into existing settings — to remove a
              key, contact platform support.
            </p>
          </div>
          {error && (
            <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}
          {savedAt && (
            <p className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700">
              Saved.
            </p>
          )}
          <div className="flex justify-end">
            <Button onClick={save} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
